import React from 'react';
import {
  CimmplePayComparisonCategory,
  cimmplePayPlans,
} from '../../data/cimmplePayPricing';
import { productTheme } from '../../utils/productTheme';

interface CimmplePayComparisonTableProps {
  categories: CimmplePayComparisonCategory[];
}

const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const CimmplePayComparisonTable: React.FC<CimmplePayComparisonTableProps> = ({
  categories,
}) => {
  const theme = productTheme('accent');
  const planLabels = cimmplePayPlans.map((p) => p.name);

  return (
    <div className="overflow-x-auto -mx-4 sm:mx-0">
      <table className="w-full min-w-[640px] border-collapse text-left">
        <caption className="sr-only">
          CimmplePay feature comparison across Starter, Growth, and Pro plans
        </caption>
        <thead>
          <tr className="border-b border-slate-200 dark:border-dark-700">
            <th
              scope="col"
              className="py-4 pl-4 sm:pl-0 pr-4 text-sm font-semibold text-slate-500 dark:text-dark-400 w-[40%]"
            >
              Feature
            </th>
            {planLabels.map((label) => (
              <th
                key={label}
                scope="col"
                className={`py-4 px-3 text-center text-sm font-display font-bold ${
                  label === 'Growth'
                    ? theme.text
                    : 'text-slate-900 dark:text-white'
                }`}
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {categories.map((category) => (
            <React.Fragment key={category.name}>
              <tr className="bg-slate-50 dark:bg-dark-800/40">
                <th
                  colSpan={4}
                  scope="colgroup"
                  className="py-3 pl-4 sm:pl-0 pr-4 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-dark-400"
                >
                  {category.name}
                </th>
              </tr>
              {category.features.map((feature) => (
                <tr
                  key={feature.name}
                  className="border-b border-slate-100 dark:border-dark-800/80"
                >
                  <th
                    scope="row"
                    className="py-3.5 pl-4 sm:pl-0 pr-4 text-sm font-medium text-slate-700 dark:text-dark-300"
                  >
                    {feature.name}
                  </th>
                  {(
                    [
                      ['starter', feature.starter],
                      ['growth', feature.growth],
                      ['pro', feature.pro],
                    ] as const
                  ).map(([key, included]) => (
                    <td key={key} className="py-3.5 px-3 text-center">
                      {included ? (
                        <span className="inline-flex items-center justify-center">
                          <CheckIcon
                            className={`w-5 h-5 ${
                              key === 'growth'
                                ? theme.text
                                : 'text-green-600 dark:text-green-500'
                            }`}
                          />
                          <span className="sr-only">Included</span>
                        </span>
                      ) : (
                        <span className="text-slate-300 dark:text-dark-600" aria-label="Not included">
                          —
                        </span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CimmplePayComparisonTable;
